import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Col, Row, Card, CardHeader, CardBody, Table, Button, Modal, ModalBody, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BookingInfo from "./components/BookingInfo";
import { getActiveColorStyle } from "utils/colorUtils";
import { formatDateTimeForDisplay } from "utils/dateUtils";
import { formatAccountDescriptionDisplay, getAccountDescriptionValue } from "utils/accountTypeLabels";
import IdentityInformationForm from "components/Customers/IdentityInformationForm";
import DetailedBorrowerForm from "components/Customers/DetailedBorrowerForm";
import { useGetWorkingCalculuses, useGetCalculusDetail, useCreateCalculus, useUpdateCalculus, useDeleteCalculus } from "api/calculuses/service";
import { useCreateCustomer } from "api/customers/service";
import Collateral from "./components/Collateral";
import LoanBalance from "./components/LoanBalance";
import { formatDateForAPI } from "utils/dateUtils";
import { useUpdateInventory } from "api/inventory/service";
import { useNotification } from "contexts/NotificationContext";
import classNames from "classnames";
import SubHeader from "components/Navbars/SubHeader";
import {
  getTheme2FormFieldsCss,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";


const AddAccount = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const isTheme2 = shellThemeVariant === "2";
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? `${getTheme2FormFieldsCss({
            scopeSelector: ".add-account-page.add-account-page--theme2",
            fieldsSelector: ".add-account-form-fields",
          })}
          .add-account-page.add-account-page--theme2 .table,
          .add-account-page.add-account-page--theme2 .table th,
          .add-account-page.add-account-page--theme2 .table td {
            font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
          }
          .add-account-page.add-account-page--theme2 .custom-tabs .nav-link {
            font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
          }
          .add-account-page.add-account-page--theme2 .add-account-card-header h2 {
            font-size: calc(${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} + 4px) !important;
          }`
        : "",
    [isTheme2]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const bookingIdFromUrl = searchParams.get('id');
  const [showWizard, setShowWizard] = useState(() => !!bookingIdFromUrl);
  const { data: calculuses, isLoading } = useGetWorkingCalculuses();
  const createCalculus = useCreateCalculus();
  const updateCalculus = useUpdateCalculus();
  const deleteCalculus = useDeleteCalculus();
  const createCustomer = useCreateCustomer();
  const { data: calculusDetail, isLoading: isLoadingDetail } = useGetCalculusDetail(bookingIdFromUrl);
  const updateInventoryMutation = useUpdateInventory();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const [activeTab, setActiveTab] = useState("BOOKING_INFO");
  const bookingInfoRef = useRef(null);
  const coBorrowerRef = useRef(null);
  const detailedBorrowerRef = useRef(null);
  const collateralRef = useRef(null);
  const didSetInitialTabRef = useRef(false);
  const [bookingFormData, setBookingFormData] = useState({
    loanNumber: null,
    portfolioId: null,
    accountDescription: '',
    servicingBranch: null,
    lenderId: null,
    source: null
  });

  const [borrowerSelectData, setBorrowerSelectData] = useState({
    ssn: "",
    date_of_birth: "",
    full_name: "",
    gender: "",
    active_service_member: false,
    new_customer: true,
    customer_id: null,
  });

  // Co-borrower identity select state
  const [coBorrowerSelectData, setCoBorrowerSelectData] = useState({
    ssn: "",
    date_of_birth: "",
    full_name: "",
    gender: "",
    active_service_member: false,
    new_customer: true,
    customer_id: null,
  });

  // Collateral form data state
  const [collateralFormData, setCollateralFormData] = useState({
    documentId:"",
    vin: "",
    year: "",
    make: "",
    model: "",
    trim: "",
    stockNumber: "",
    mileage: "",
    mileageStatus: "",
    exteriorColor: "",
    interiorColor: "",
    engine: "",
    bodyStyle: "",
    transmission: "",
    driveType: "",
    condition: "",
    licensePlate: "",
    valNumber: "",
    installedOptions: "",
    additional_vehicle_info: {}
  });

console.log("collateralFormData", collateralFormData);

  // Collateral exists state
  const [collateralExists, setCollateralExists] = useState(null);

  // Loan balance form data state
  const [loanBalanceFormData, setLoanBalanceFormData] = useState({
    contract: "",
    compoundingMethod: "",
    accrualMethod: "",
    salesTaxDeferred: "",
    daysInYear: "",
    amortization: "",
    loanRate: "0.0000%",
    apr: "0.0000%",
    financeCharge: "$0.00",
    amountFinanced: "$0.00",
    totalOfPayments: "$0.00",
    totalDownPayment: "$12.00",
  });

  // Memoize the form change handler to prevent unnecessary re-renders
  const handleBookingFormChange = useCallback((formData) => {
    setBookingFormData(formData);
  }, []);

  // Tab configuration
  const tabs = [
    { id: "BOOKING_INFO", name: t('addAccount.bookingInfo'), order: 0 },
    { id: "BORROWER_INFO", name: t('addAccount.borrowerInfo'), order: 1 },
    { id: "CO_BORROWER_INFO", name: t('addAccount.coBorrowerInfo'), order: 3 },
    { id: "COLLATERAL", name: t('addAccount.collateral'), order: 5 },
    { id: "LOAN_BALANCE", name: t('addAccount.loanBalance'), order: 6 }
  ];

  useEffect(() => {
    if (!bookingIdFromUrl) {
      setShowWizard(false);
      return;
    }
    setShowWizard(true);
  }, [bookingIdFromUrl]);

  // Check if tab is accessible based on creation_order
  const isTabAccessible = (tabId) => {
    if (!bookingIdFromUrl) {
      // No ID: only BOOKING_INFO
      return tabId === "BOOKING_INFO";
    }

    const creationOrder = calculusDetail ? Number(calculusDetail.creation_order) : 0;
    // New rule: once creation_order >= 2, all tabs are accessible
    if (creationOrder >= 2) {
      return true;
    }
    const target = tabs.find(t => t.id === tabId);
    const isAccessible = target ? target.order <= creationOrder : false;
    return isAccessible;
  };

  // Handle tab changes and API calls
  const handleTabChange = async (tabId) => {
    if (!isTabAccessible(tabId)) {
      return;
    }
    setActiveTab(tabId);
  };

  const handleCurrentTabSave = async () => {
    try {
      if (activeTab === "BOOKING_INFO") {
        const payload = {
          description: bookingFormData.accountDescription,
          act_number: bookingFormData.loanNumber,
          portfolio: bookingFormData.portfolioId,
          servicing_branch: Number(bookingFormData.servicingBranch),
          lender: bookingFormData.lenderId || null,
          source: bookingFormData.source,
        };


        await updateCalculus.mutateAsync({ id: bookingIdFromUrl, data: payload });
      } else if (activeTab === "BORROWER_INFO") {
        if (detailedBorrowerRef.current && typeof detailedBorrowerRef.current.updateCustomer === 'function') {
          await detailedBorrowerRef.current.updateCustomer();
        }
      } else if (activeTab === "CO_BORROWER_INFO") {
        if (coBorrowerRef.current && typeof coBorrowerRef.current.updateCustomer === 'function') {
          await coBorrowerRef.current.updateCustomer();
        }
      } else if (activeTab === "COLLATERAL") {
        // Handle collateral save logic here (same as in the main flow)
        if (calculusDetail?.inventory) {
          const inventoryVin = calculusDetail.inventory.inventory_vehicle?.vin;
          const formVin = collateralFormData.vin;
          const vinChanged = inventoryVin && formVin && inventoryVin !== formVin;

          if (vinChanged) {
            await updateCalculus.mutateAsync({
              id: bookingIdFromUrl,
              data: {
                collateral: {
                  vin: collateralFormData.vin,
                  model: collateralFormData.model,
                  make: collateralFormData.make,
                  year: collateralFormData.year,
                  trim: collateralFormData.trim,
                  exterior_color: collateralFormData.exteriorColor,
                  interior_color: collateralFormData.interiorColor,
                  body_style: collateralFormData.bodyStyle,
                  drive_type: collateralFormData.driveType,
                  actual_mileage: collateralFormData.mileage,
                  actual_mileage_status: collateralFormData.mileageStatus,
                  engine: collateralFormData.engine,
                  transmission: collateralFormData.transmission,
                  stock_number: collateralFormData.stockNumber,
                  condition: collateralFormData.condition,
                  license_plate: collateralFormData.licensePlate,
                  val_number: collateralFormData.valNumber,
                  installed_options: collateralFormData.installedOptions
                }
              }
            });
          } else {
            const updateData = {
              vehicle: {
                model: collateralFormData?.model || "",
                make: collateralFormData?.make || "",
                year: collateralFormData?.year || "",
                trim: collateralFormData?.trim || "",
                exterior_color: collateralFormData?.exterior_color || "",
                interior_color: collateralFormData?.interior_color || "",
                body_style: collateralFormData?.body_style || "",
                drive_type: collateralFormData?.drive_type || "",
                actual_mileage: collateralFormData?.mileage || "",
                actual_mileage_status: collateralFormData?.mileageStatus || "",
                engine: collateralFormData?.engine || "",
                transmission: collateralFormData?.transmission || "",
                stock_number: collateralFormData?.stockNumber || "",
                condition: collateralFormData?.condition || "",
                license_plate: collateralFormData?.licensePlate || "",
                val_number: collateralFormData?.valNumber || "",
                installed_options: collateralFormData?.installedOptions || ""
              }
            }
            updateInventoryMutation.mutate({ id: calculusDetail.inventory.documentId, data: updateData });
          }
        } else {
          await updateCalculus.mutateAsync({
            id: bookingIdFromUrl,
            data: {
              collateral: {
                vin: collateralFormData.vin,
                model: collateralFormData.model,
                make: collateralFormData.make,
                year: collateralFormData.year,
                trim: collateralFormData.trim,
                exterior_color: collateralFormData.exteriorColor,
                interior_color: collateralFormData.interiorColor,
                body_style: collateralFormData.bodyStyle,
                drive_type: collateralFormData.driveType,
                actual_mileage: collateralFormData.mileage,
                actual_mileage_status: collateralFormData.mileageStatus,
                engine: collateralFormData.engine,
                transmission: collateralFormData.transmission,
                stock_number: collateralFormData.stockNumber,
                condition: collateralFormData.condition,
                license_plate: collateralFormData.licensePlate,
                val_number: collateralFormData.valNumber,
                installed_options: collateralFormData.installedOptions
              }
            }
          });
        }
      } else if (activeTab === "LOAN_BALANCE") {
        // Handle loan balance save logic
        const loanPayload = {
          contract: loanBalanceFormData.contract || "",
          loan_rate: parseFloat(loanBalanceFormData.loanRate?.replace('%', '')) || 0,
          apr: parseFloat(loanBalanceFormData.apr?.replace('%', '')) || 0,
          finance_charge: parseFloat(loanBalanceFormData.financeCharge?.replace('$', '').replace(',', '')) || 0,
          amount_financed: parseFloat(loanBalanceFormData.amountFinanced?.replace('$', '').replace(',', '')) || 0,
          total_payments: parseFloat(loanBalanceFormData.totalOfPayments?.replace('$', '').replace(',', '')) || 0,
          total_down_payment: parseFloat(loanBalanceFormData.totalDownPayment?.replace('$', '').replace(',', '')) || 0,
          contract_date: loanBalanceFormData.contractDate ? formatDateForAPI(loanBalanceFormData.contractDate) : null,
          original_finance_charge: parseFloat(loanBalanceFormData.originalFinanceCharge?.replace('$', '').replace(',', '')) || 0,
          original_amt_financed: parseFloat(loanBalanceFormData.originalAmtFinanced?.replace('$', '').replace(',', '')) || 0,
          original_sales_tax: parseFloat(loanBalanceFormData.originalSalesTax?.replace('$', '').replace(',', '')) || 0,
          current_discount_amount: parseFloat(loanBalanceFormData.currentDiscountAmount?.replace('$', '').replace(',', '')) || 0,
          discount_method: loanBalanceFormData.discountMethod === "(Straight-Line) Recognize Discount As Percentage Of Payment" ? "straight_line" : "pro_rata",
          down_payment_schedule: loanBalanceFormData.downPaymentSchedules || [],
          payment_schedule: loanBalanceFormData.paymentSchedules || [],

        };

        await updateCalculus.mutateAsync({
          id: bookingIdFromUrl,
          data: { loan: loanPayload, create: true }
        });
      }
    } catch (error) {
      console.error('Error saving current tab data:', error);
    }
  };

  const handleNextButtonClick = async () => {
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);

    if (currentIndex >= tabs.length) return;

    const nextTab = tabs[currentIndex + 1];

    const orderIndex = calculusDetail ? Number(calculusDetail.creation_order) : 0;
    const currentTab = tabs.find(tab => tab.id === activeTab);

    // If creation_order is greater than current tab's order, only save (don't advance)
    if (currentTab && orderIndex > currentTab.order) {
      // Just save the current tab data without advancing
      await handleCurrentTabSave();
      return;
    }

    if (activeTab === "BOOKING_INFO" && !bookingIdFromUrl) {
      try {
        const formData = {
          accountDescription: bookingFormData.accountDescription || '',
          loanNumber: (bookingFormData.loanNumber || '').trim(),
          portfolioId: bookingFormData.portfolioId || null,
          servicingBranch: bookingFormData.servicingBranch || null,
          lenderId: bookingFormData.lenderId || null,
          source: bookingFormData.source,
        };

        if (!formData.loanNumber || !formData.portfolioId) {
          alert(t('addAccount.pleaseFillLoanNumberAndPortfolio'));
          return;
        }

        const payload = {
          act_status: "working",
          creation_order: "1",
          description: formData.accountDescription,
          act_number: formData.loanNumber,
          portfolio: formData.portfolioId,
          servicing_branch: Number(formData.servicingBranch),
          lender: formData.lenderId || null,
          source: formData.source,
        };

        const result = await createCalculus.mutateAsync(payload);
        const created = result?.data || result || {};
        const bookingId = created.booking_id;

        if (bookingId) {
          // URL'ye id yaz
          const next = new URLSearchParams(searchParams);
          next.set('id', String(bookingId));
          setSearchParams(next, { replace: true });

          // Kullanıcı deneyimi için anında ikinci taba geç (URL değişimi de zaten tetikleyecek)
          setActiveTab("BORROWER_INFO");
        }
      } catch (error) {
        console.error('Error creating account:', error);
        alert(t('addAccount.errorCreatingAccount'));
      }
      return; // Özel durum tamamlandı
    }

    if (activeTab === "BOOKING_INFO" && bookingIdFromUrl) {
      try {
        const formData = {
          accountDescription: bookingFormData.accountDescription || '',
          loanNumber: (bookingFormData.loanNumber || '').trim(),
          portfolioId: bookingFormData.portfolioId || null,
          servicingBranch: bookingFormData.servicingBranch || null,
          lenderId: bookingFormData.lenderId || null,
          source: bookingFormData.source,
        };

        if (!formData.loanNumber || !formData.portfolioId) {
          alert(t('addAccount.pleaseFillLoanNumberAndPortfolio'));
          return;
        }

        const payload = {
          // act_status ve creation_order dokunmadan, yalnız içerik güncelle
          description: formData.accountDescription,
          act_number: formData.loanNumber,
          portfolio: formData.portfolioId,
          servicing_branch: Number(formData.servicingBranch),
          lender: formData.lenderId || null,
          source: formData.source,
        };

        await updateCalculus.mutateAsync({ id: bookingIdFromUrl, data: payload });
        // Sonraki taba geç
        if (isTabAccessible(nextTab.id)) {
          handleTabChange(nextTab.id);
        }
      } catch (error) {
        console.error('Error updating booking info:', error);
        alert(t('addAccount.errorUpdatingBookingInfo'));
      }
      return;
    }


    if (activeTab === "LOAN_BALANCE" && bookingIdFromUrl) {
      // Require collateral before booking
      if (!calculusDetail?.inventory) {
        showNotification('warning', t('addAccount.youHaveNotAddedCollateral'));
        setActiveTab('COLLATERAL');
        return;
      }
      try {
        const loanPayload = {
          contract: loanBalanceFormData.contract || "",
          loan_rate: parseFloat(loanBalanceFormData.loanRate?.replace('%', '')) || 0,
          apr: parseFloat(loanBalanceFormData.apr?.replace('%', '')) || 0,
          finance_charge: parseFloat(loanBalanceFormData.financeCharge?.replace('$', '').replace(',', '')) || 0,
          amount_financed: parseFloat(loanBalanceFormData.amountFinanced?.replace('$', '').replace(',', '')) || 0,
          total_payments: parseFloat(loanBalanceFormData.totalOfPayments?.replace('$', '').replace(',', '')) || 0,
          total_down_payment: parseFloat(loanBalanceFormData.totalDownPayment?.replace('$', '').replace(',', '')) || 0,
          contract_date: loanBalanceFormData.contractDate ? formatDateForAPI(loanBalanceFormData.contractDate) : null,
          original_finance_charge: parseFloat(loanBalanceFormData.originalFinanceCharge?.replace('$', '').replace(',', '')) || 0,
          original_amt_financed: parseFloat(loanBalanceFormData.originalAmtFinanced?.replace('$', '').replace(',', '')) || 0,
          original_sales_tax: parseFloat(loanBalanceFormData.originalSalesTax?.replace('$', '').replace(',', '')) || 0,
          current_discount_amount: parseFloat(loanBalanceFormData.currentDiscountAmount?.replace('$', '').replace(',', '')) || 0,
          discount_method: loanBalanceFormData.discountMethod === "(Straight-Line) Recognize Discount As Percentage Of Payment" ? "straight_line" : "pro_rata",
          down_payment_schedule: loanBalanceFormData.downPaymentSchedules || [],
          payment_schedule: loanBalanceFormData.paymentSchedules || []
        };

        const result = await updateCalculus.mutateAsync({
          id: bookingIdFromUrl,
          data: {
            loan: loanPayload,
            act_status: "booked" // Mark as booked when completing loan balance
          }
        });

        if (result) {
          navigate(`/accounts/${result.booking_id}`);
        }

      } catch (error) {
        console.error('Error booking loan account:', error);
        console.error('Error details:', error.response?.data || error.message);
      }
    }

    if (activeTab === "BORROWER_INFO") {
      const orderIndex = calculusDetail ? Number(calculusDetail.creation_order) : 0;
      if (orderIndex === 1) {

        try {
          await updateCalculus.mutateAsync({
            id: bookingIdFromUrl,
            data: {
              customer: {
                new_customer: true,
                booking_id: calculusDetail?.id,
                ssn: borrowerSelectData.ssn,
                date_of_birth: formatDateForAPI(borrowerSelectData.date_of_birth),
                full_name: borrowerSelectData.full_name,
                gender: borrowerSelectData.gender,
                active_service_member: borrowerSelectData.active_service_member,
              },
              creation_order: "2"
            }
          });
          handleTabChange(nextTab.id);
        } catch (error) {
          console.error('Error creating customer and updating calculus:', error);
          alert(t('addAccount.errorCreatingCustomer'));
        }
      } else if (orderIndex >= 2 && detailedBorrowerRef.current && typeof detailedBorrowerRef.current.updateCustomer === 'function') {
        try {
          const ok = await detailedBorrowerRef.current.updateCustomer();
          if (ok) {
            await handleDetailedBorrowerUpdate();
            handleTabChange(nextTab.id);
          }
        } catch (error) {
          console.error('Error updating borrower info:', error);
        }
      } else {
        handleTabChange(nextTab.id);
      }
    } else if (activeTab === "CO_BORROWER_INFO") {
      const orderIndex = calculusDetail ? Number(calculusDetail.creation_order) : 0;
      if (orderIndex === 3) {
        try {
          await updateCalculus.mutateAsync({
            id: bookingIdFromUrl,
            data: {
              co_customer: {
                booking_id: calculusDetail?.id,
                ssn: coBorrowerSelectData.ssn,
                date_of_birth: formatDateForAPI(coBorrowerSelectData.date_of_birth),
                full_name: coBorrowerSelectData.full_name,
                gender: coBorrowerSelectData.gender,
                active_service_member: coBorrowerSelectData.active_service_member,
              },
              creation_order: "4"
            }
          });
          handleTabChange(nextTab.id);
        } catch (error) {
          console.error('Error creating co-borrower and updating calculus:', error);
        }
      } else if (orderIndex >= 4 && coBorrowerRef.current && typeof coBorrowerRef.current.updateCustomer === 'function') {
        try {
          const ok = await coBorrowerRef.current.updateCustomer();
          if (ok) {
            // Move order to 4 after successful co-borrower update
            await updateCalculus.mutateAsync({
              id: bookingIdFromUrl,
              data: { creation_order: "4" }
            });
            handleTabChange(nextTab.id);
          }
        } catch (error) {
          console.error('Error updating co-borrower info:', error);
        }
      } else {
        handleTabChange(nextTab.id);
      }
    } else if (activeTab === "COLLATERAL") {
      const orderIndex = calculusDetail ? Number(calculusDetail.creation_order) : 0;

      // Check if we have inventory data to use as collateral
      if (calculusDetail?.inventory) {

        // Check if VIN is different from inventory VIN
        const inventoryVin = calculusDetail.inventory.inventory_vehicle?.vin;
        const formVin = collateralFormData.vin;
        const vinChanged = inventoryVin && formVin && inventoryVin !== formVin;

        if (vinChanged) {
          try {
            await updateCalculus.mutateAsync({
              id: bookingIdFromUrl,
              data: {
                collateral: {
                  vin: collateralFormData.vin,
                  model: collateralFormData.model,
                  make: collateralFormData.make,
                  year: collateralFormData.year,
                  trim: collateralFormData.trim,
                  exterior_color: collateralFormData.exteriorColor,
                  interior_color: collateralFormData.interiorColor,
                  body_style: collateralFormData.bodyStyle,
                  drive_type: collateralFormData.driveType,
                  actual_mileage: collateralFormData.mileage,
                  actual_mileage_status: collateralFormData.mileageStatus,
                  engine: collateralFormData.engine,
                  transmission: collateralFormData.transmission,
                  stock_number: collateralFormData.stockNumber,
                  condition: collateralFormData.condition,
                  license_plate: collateralFormData.licensePlate,
                  val_number: collateralFormData.valNumber,
                  installed_options: collateralFormData.installedOptions
                }
              }
            });
            handleTabChange(nextTab.id);
          } catch (error) {
            console.error('Error updating calculus with new collateral data:', error);
          }
        } else {
          try {
            const updateData = {
              vehicle: {
                model: collateralFormData?.model || "",
                make: collateralFormData?.make || "",
                year: collateralFormData?.year || "",
                trim: collateralFormData?.trim || "",
                exterior_color: collateralFormData?.exterior_color || "",
                interior_color: collateralFormData?.interior_color || "",
                body_style: collateralFormData?.body_style || "",
                drive_type: collateralFormData?.drive_type || "",
                actual_mileage: collateralFormData?.mileage || "",
                actual_mileage_status: collateralFormData?.mileageStatus || "",
                engine: collateralFormData?.engine || "",
                transmission: collateralFormData?.transmission || "",
                stock_number: collateralFormData?.stockNumber || "",
                condition: collateralFormData?.condition || "",
                license_plate: collateralFormData?.licensePlate || "",
                val_number: collateralFormData?.valNumber || "",
                installed_options: collateralFormData?.installedOptions || ""
              }
            }
            updateInventoryMutation.mutate({ id: calculusDetail.inventory.documentId, data: updateData })
            handleTabChange(nextTab.id);
          } catch (error) {
            console.error('Error updating inventory:', error);
          }
        }
      } else {
        try {
          await updateCalculus.mutateAsync({
            id: bookingIdFromUrl,
            data: {
              collateral: {
                documentId: collateralFormData.documentId,
                vin: collateralFormData.vin,
                model: collateralFormData.model,
                make: collateralFormData.make,
                year: collateralFormData.year,
                trim: collateralFormData.trim,
                exterior_color: collateralFormData.exteriorColor,
                interior_color: collateralFormData.interiorColor,
                body_style: collateralFormData.bodyStyle,
                drive_type: collateralFormData.driveType,
                actual_mileage: collateralFormData.mileage,
                actual_mileage_status: collateralFormData.mileageStatus,
                engine: collateralFormData.engine,
                transmission: collateralFormData.transmission,
                stock_number: collateralFormData.stockNumber,
                condition: collateralFormData.condition,
                license_plate: collateralFormData.licensePlate,
                val_number: collateralFormData.valNumber,
                installed_options: collateralFormData.installedOptions
              }
            }
          });
          handleTabChange(nextTab.id);
        } catch (error) {
          console.error('Error creating collateral:', error);
        }
      }
    } else {
      handleTabChange(nextTab?.id);
    }
    if (!isTabAccessible(nextTab?.id)) return;
  };
  // Check if Next button should be enabled
  const isNextButtonEnabled = () => {
    // Special case for LOAN_BALANCE tab - Book button should always be enabled
    if (activeTab === "LOAN_BALANCE") {
      return true;
    }

    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
    if (currentIndex >= tabs.length - 1) return false;

    const nextTab = tabs[currentIndex + 1];
    const orderIndex = calculusDetail ? Number(calculusDetail.creation_order) : 0;
    const currentTab = tabs.find(tab => tab.id === activeTab);

    // If creation_order is greater than current tab's order, only enable save (don't advance)
    if (currentTab && orderIndex > currentTab.order) {
      return true; // Enable button for save only
    }

    // Special validation for BOOKING_INFO tab
    if (activeTab === "BOOKING_INFO" && !bookingIdFromUrl) {
      const hasLoanNumber = bookingFormData.loanNumber && bookingFormData.loanNumber.trim() !== "";
      const hasPortfolio = bookingFormData.portfolioId && bookingFormData.portfolioId !== null;
      const isValid = hasLoanNumber && hasPortfolio;
      return isValid;
    }

    // Special validation for BORROWER_INFO tab when creation_order is 1
    if (activeTab === "BORROWER_INFO") {
      if (orderIndex === 1) {
        // Check if Full Name, SSN, and DOB are filled
        const hasFullName = borrowerSelectData.full_name && borrowerSelectData.full_name.trim() !== "";
        const hasSSN = borrowerSelectData.ssn && borrowerSelectData.ssn.trim() !== "";
        const hasDOB = borrowerSelectData.date_of_birth;
        return hasFullName && hasSSN && hasDOB;
      }
    }

    // Special validation for CO_BORROWER_INFO tab when creation_order is 3
    if (activeTab === "CO_BORROWER_INFO") {
      if (orderIndex === 3) {
        // Check if Full Name, SSN, and DOB are filled
        const hasFullName = coBorrowerSelectData.full_name && coBorrowerSelectData.full_name.trim() !== "";
        const hasSSN = coBorrowerSelectData.ssn && coBorrowerSelectData.ssn.trim() !== "";
        const hasDOB = coBorrowerSelectData.date_of_birth;
        return hasFullName && hasSSN && hasDOB;
      }
    }

    // Special validation for COLLATERAL tab
    if (activeTab === "COLLATERAL") {
      // Check required fields: VIN, Year, Make, Model, Stock Number
      const hasVin = collateralFormData.vin && collateralFormData.vin.trim() !== "";
      const hasYear = collateralFormData.year && collateralFormData.year.trim() !== "";
      const hasMake = collateralFormData.make && collateralFormData.make.trim() !== "";
      const hasModel = collateralFormData.model && collateralFormData.model.trim() !== "";
      const hasStockNumber = collateralFormData.stockNumber && collateralFormData.stockNumber.trim() !== "";
      return hasVin && hasYear && hasMake && hasModel && hasStockNumber;
    }

    if (!isTabAccessible(nextTab.id)) return false;
    return true;
  };

  const nextButtonEnabled = isNextButtonEnabled();

  const getButtonText = () => {
    const orderIndex = calculusDetail ? Number(calculusDetail.creation_order) : 0;
    const currentTab = tabs.find(tab => tab.id === activeTab);

    if (currentTab && orderIndex > currentTab.order) {
      return t('addAccount.save');
    }

    return activeTab === "LOAN_BALANCE" ? t('addAccount.book') : t('addAccount.next');
  };


  const [identityData, setIdentityData] = useState({
    ssn: "",
    date_of_birth: "",
    full_name: "",
    gender: "",
    active_service_member: false,
    new_customer: true,
    customer_id: null,
  });


  const handleBorrowerSelectInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBorrowerSelectData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCoBorrowerSelectInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCoBorrowerSelectData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCollateralFormChange = useCallback((formData) => {
    setCollateralFormData(formData);
  }, []);

  const handleCollateralExistsChange = useCallback((exists) => {
    setCollateralExists(exists);
  }, []);

  const handleLoanBalanceFormChange = useCallback((formData) => {
    setLoanBalanceFormData(formData);
  }, []);

  const handleCustomerSelect = async (customerId) => {
    try {
      await updateCalculus.mutateAsync({
        id: bookingIdFromUrl,
        data: {
          customer: customerId,
          new_customer: false,
          creation_order: "2"
        }
      });
      // Move to next tab
      setActiveTab("BORROWER_INFO");
    } catch (error) {
      console.error('Error updating calculus with customer:', error);
    }
  };

  const handleCoCustomerSelect = async (customerId) => {
    try {
      await updateCalculus.mutateAsync({
        id: bookingIdFromUrl,
        data: {
          co_customer: customerId,
          creation_order: "4"
        }
      });
      setActiveTab("CO_BORROWER_INFO");
    } catch (error) {
      console.error('Error updating calculus with co-customer:', error);
    }
  };

  const handleDetailedBorrowerUpdate = async () => {
    try {
      // Update calculus with creation_order = 3
      await updateCalculus.mutateAsync({
        id: bookingIdFromUrl,
        data: {
          creation_order: "3"
        }
      });
      // Move to next tab
      setActiveTab("CO_BORROWER_INFO");
    } catch (error) {
      console.error('Error updating calculus:', error);
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedDeleteId) {
      deleteCalculus.mutate(selectedDeleteId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedDeleteId(null);
        }
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };


  // Set initial tab based on creation_order exactly once per booking
  useEffect(() => {
    if (!showWizard || !calculusDetail) return;

    const creationOrder = Number(calculusDetail.creation_order);
    if (Number.isNaN(creationOrder)) return;

    // Find the highest tab whose order <= creationOrder
    const candidate = [...tabs]
      .filter(t => t.order <= creationOrder)
      .sort((a, b) => b.order - a.order)[0]?.id || "BOOKING_INFO";

    if (!didSetInitialTabRef.current) {
      setActiveTab(candidate);
      didSetInitialTabRef.current = true;
      return;
    }

    // If already initialized, only move if current tab isn't accessible
    if (!isTabAccessible(activeTab)) {
      setActiveTab(candidate);
    }
  }, [showWizard, calculusDetail]);

  // Reset one-time init guard when booking ID changes
  useEffect(() => {
    didSetInitialTabRef.current = false;
  }, [bookingIdFromUrl]);

  // Reset tab on ID removal; do not override when ID appears (creation)
  useEffect(() => {
    if (!bookingIdFromUrl) {
      setActiveTab("BOOKING_INFO");
    }
  }, [bookingIdFromUrl]);


  // Use exact theme colors via CSS variables for a 1:1 match
  const workingCalculuses = (calculuses?.results || []).filter((c) => {
    const status = (c.acct_status || c.act_status || '').toLowerCase();
    return status === 'working';
  });

  const handleWizardBack = useCallback(() => {
    setShowWizard(false);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const accountStatusLabel =
    calculusDetail?.act_status || t("addAccount.preliminaryWork");

  return (
    <div
      className={classNames("content add-account-page", {
        "add-account-page--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-add-account-inset": isTheme2,
      })}
    >
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <style>
        {`
          .add-account-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .add-account-card-header h2 {
            margin-bottom: 0;
            line-height: 1.2;
          }
          .add-account-page .card .card-body {
            padding: 15px;
            padding-top: 10px;
            padding-bottom: 0 !important;
          }
        `}
      </style>
      {!showWizard ? (
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="add-account-card-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 className="mb-0">{t('addAccount.accounts')}</h2>
                  <Button size="sm" style={{ ...activeColorStyle, color: 'white' }} onClick={() => {
                    setActiveTab("BOOKING_INFO"); // Reset to first tab
                    setShowWizard(true);
                  }}>
                    {t('addAccount.addAccount')}
                  </Button>
                </div>
              </CardHeader>
              <CardBody className={classNames({ "add-account-form-fields": isTheme2 })}>
                {isLoading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "200px" }}
                  >
                    <div
                      className="spinner-border"
                      role="status"
                      style={{ color: activeColorStyle.borderColor }}
                    >
                      <span className="sr-only">{t("addAccount.loading")}</span>
                    </div>
                  </div>
                ) : workingCalculuses.length === 0 ? (
                  <div>{t('addAccount.noWorkingRecordsFound')}</div>
                ) : (
                  <Table responsive>
                    <thead className="text-primary">
                      <tr>
                        <th>{t('addAccount.id')}</th>
                        <th>{t('addAccount.borrower')}</th>
                        <th>{t('addAccount.description')}</th>
                        <th>{t('addAccount.addDate')}</th>
                        <th>{t('addAccount.lastModified')}</th>
                        <th>{t('addAccount.status')}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {workingCalculuses.map((item, idx) => (
                        <tr
                          key={item.documentId || idx}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            const bookingId = item.booking_id;
                            if (bookingId) {
                              const next = new URLSearchParams(searchParams);
                              next.set('id', String(bookingId));
                              setSearchParams(next, { replace: false });
                            }
                          }}
                        >
                          <td>{item.booking_id}</td>
                          <td>{item.customer?.full_name}</td>
                          <td>{formatAccountDescriptionDisplay(item.description, item.account_type)}</td>
                          <td>{formatDateTimeForDisplay(item.createdAt)}</td>
                          <td>{formatDateTimeForDisplay(item.updatedAt)}</td>
                          <td>{item.act_status}</td>
                          <td className="text-right">
                            <Button
                              color="danger"
                              size="sm"
                              className="btn-icon btn-link like"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(item.booking_id);
                              }}
                              disabled={deleteCalculus.isPending}
                            >
                              {deleteCalculus.isPending && selectedDeleteId === item.booking_id ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                <i className="tim-icons icon-simple-remove" />
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : (
        <Col className="mr-auto ml-auto" md="12">
          {isLoadingDetail ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "400px" }}
            >
              <div
                className="spinner-border"
                role="status"
                style={{ color: activeColorStyle.borderColor }}
              >
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <SubHeader
                activeColor={activeColor}
                title={t("addAccount.newAccount")}
                onBack={handleWizardBack}
                status={accountStatusLabel}
                right={
                  <span
                    style={{
                      color: "white",
                      fontSize: isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("addAccount.bookingId")} {bookingIdFromUrl || "0"}
                  </span>
                }
                theme2ScrollGapFill={shellThemeVariant === "2"}
                isLightTheme={
                  typeof document !== "undefined" &&
                  document.body.classList.contains("white-content")
                }
              />
              <div
                className={classNames("wizard-theme-override", {
                  "add-account-form-fields": isTheme2,
                })}
                style={{
                  "--wiz-color": activeColorStyle.borderColor,
                  "--wiz-bg": activeColorStyle.backgroundImage,
                }}
              >
              <div className="custom-tabs">
                {/* Tab Navigation */}
                <div className="d-flex align-items-center justify-content-between mb-0">
                  <Nav tabs className="border-0" style={{ borderBottom: "none" }}>
                    {tabs.map(tab => {
                      const isDisabled = !isTabAccessible(tab.id);
                      const isActive = activeTab === tab.id;

                      return (
                        <NavItem key={tab.id}>
                          <NavLink
                            href="#"
                            className={isActive ? "active font-weight-bold" : "text-muted"}
                            style={{
                              background: "none",
                              border: "none",
                              position: "relative",
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              color: isDisabled ? '#adb5bd' : (isActive ? activeColorStyle.borderColor : undefined),
                              fontWeight: isActive ? 700 : 500,
                              opacity: isDisabled ? 0.5 : 1
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isDisabled) {
                                handleTabChange(tab.id);
                              }
                            }}
                          >
                            {tab.name}
                            {isActive && !isDisabled && (
                              <div style={{
                                height: 3,
                                background: activeColorStyle.borderColor,
                                borderRadius: 2,
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: -8
                              }} />
                            )}
                          </NavLink>
                        </NavItem>
                      );
                    })}
                  </Nav>

                  {/* Navigation Buttons */}
                  <div className="d-flex gap-2">
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={() => {
                        const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
                        if (currentIndex > 0) {
                          const prevTab = tabs[currentIndex - 1];
                          if (isTabAccessible(prevTab.id)) {
                            handleTabChange(prevTab.id);
                          }
                        }
                      }}
                      disabled={(() => {
                        const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
                        if (currentIndex <= 0) return true;
                        const prevTab = tabs[currentIndex - 1];
                        return !isTabAccessible(prevTab.id);
                      })()}
                    >
                      {t('addAccount.previous')}
                    </Button>

                    <Button
                      size="sm"
                      style={activeColorStyle}
                      onClick={handleNextButtonClick}
                      disabled={!nextButtonEnabled}
                    >
                      {getButtonText()}
                    </Button>
                  </div>
                </div>

                {/* Tab Content */}
                <TabContent
                  activeTab={activeTab}
                  className={classNames({ "add-account-form-fields": isTheme2 })}
                >
                  <TabPane tabId={activeTab}>
                    {activeTab === "BOOKING_INFO" && (
                      <BookingInfo
                        ref={bookingInfoRef}
                        activeColor={activeColor}
                        isTheme2={isTheme2}
                        onFormChange={handleBookingFormChange}
                        onCreateCalculus={async (bookingState) => {
                          // If we already have an id from URL, don't create again
                          if (bookingIdFromUrl) {
                            return true;
                          }
                          const description = bookingState.accountDescription || '';
                          const payload = {
                            act_status: "working",
                            creation_order: "1",
                            description,
                            act_number: bookingState.loanNumber,
                            portfolio: bookingState.portfolioId,
                            servicing_branch: Number(bookingState.servicingBranch),
                            lender: bookingState.lenderId || null,
                            source: bookingState.source,
                          };
                          try {
                            const result = await createCalculus.mutateAsync(payload);
                            const created = result?.data || result || {};
                            const bookingId = created.booking_id;
                            if (bookingId) {
                              const next = new URLSearchParams(searchParams);
                              next.set('id', String(bookingId));
                              setSearchParams(next, { replace: true });
                            }
                            return true;
                          } catch (e) {
                            return false;
                          }
                        }}
                        initialValues={calculusDetail ? {
                          accountDescription: getAccountDescriptionValue(
                            calculusDetail.description,
                            calculusDetail.account_type
                          ),
                          act_number: calculusDetail.act_number || '',
                          portfolioId: calculusDetail.portfolio?.id || null,
                          servicingBranch: calculusDetail.servicing_branch,
                          lenderId: calculusDetail.lender?.id || null,
                          source: calculusDetail.source?.id
                        } : undefined}
                        prefillKey={calculusDetail ? `${calculusDetail.id}-${calculusDetail.updatedAt}` : undefined}
                        skipValidation={!!bookingIdFromUrl}
                      />
                    )}

                    {activeTab === "BORROWER_INFO" && (() => {
                      const orderIndex = calculusDetail ? Number(calculusDetail.creation_order) : 1;

                      if (orderIndex >= 2) {
                        // Show Borrower Info (detailed form for existing customer)
                        return (
                          <DetailedBorrowerForm
                            ref={detailedBorrowerRef}
                            customerData={calculusDetail?.customer}
                            showValidation={true}
                            title={t('addAccount.detailedBorrowerInformation')}
                            activeColor={activeColor}
                            isTheme2={isTheme2}
                          />
                        );
                      } else {
                        // Show Borrower Select (select existing customer)
                        return (
                          <div className="card">
                            <div className="card-body">
                              <IdentityInformationForm
                                identityData={borrowerSelectData}
                                onChange={handleBorrowerSelectInputChange}
                                showValidation={true}
                                showMatches={true}
                                onCustomerSelect={handleCustomerSelect}
                                title={t('addAccount.selectExistingBorrower')}
                                activeColor={activeColor}
                                isTheme2={isTheme2}
                              />
                            </div>
                          </div>
                        );
                      }
                    })()}

                    {activeTab === "CO_BORROWER_INFO" && (() => {
                      const orderIndex = calculusDetail ? Number(calculusDetail.creation_order) : 1;
                      if (orderIndex >= 4) {
                        return (
                          <DetailedBorrowerForm
                            ref={coBorrowerRef}
                            customerData={calculusDetail?.co_customer}
                            showValidation={true}
                            title={t('addAccount.coBorrowerInformation')}
                            asCoBorrower={true}
                            activeColor={activeColor}
                            isTheme2={isTheme2}
                          />
                        );
                      }
                      return (
                        <div className="card">
                          <div className="card-body">
                            <IdentityInformationForm
                              identityData={coBorrowerSelectData}
                              onChange={handleCoBorrowerSelectInputChange}
                              showValidation={true}
                              showMatches={true}
                              onCustomerSelect={handleCoCustomerSelect}
                              title={t('addAccount.selectExistingCoBorrower')}
                              activeColor={activeColor}
                              isTheme2={isTheme2}
                            />
                          </div>
                        </div>
                      );
                    })()}
                    {activeTab === "COLLATERAL" && (
                      <Collateral
                        ref={collateralRef}
                        activeColor={activeColor}
                        isTheme2={isTheme2}
                        onFormChange={handleCollateralFormChange}
                        onExistsChange={handleCollateralExistsChange}
                        initialValues={calculusDetail}
                        prefillKey={calculusDetail ? `${calculusDetail.id}-${calculusDetail.updatedAt}` : undefined}
                      />
                    )}
                    {activeTab === "LOAN_BALANCE" && (
                      <LoanBalance
                        isTheme2={isTheme2}
                        onFormChange={handleLoanBalanceFormChange}
                        initialValues={calculusDetail}
                        prefillKey={calculusDetail ? `${calculusDetail.id}-${calculusDetail.updatedAt}` : undefined}
                      />
                    )}
                  </TabPane>
                </TabContent>
              </div>
            </div>
            </>
          )}
        </Col>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered={false} style={{ marginTop: '100px' }}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t('addAccount.deleteBooking')}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t('addAccount.areYouSureDeleteBooking')}</p>
          <div className="modal-footer">
            <Button
              color="secondary"
              onClick={handleCancelDelete}
              disabled={deleteCalculus.isPending}
            >
              {t('addAccount.cancel')}
            </Button>
            <Button
              color="danger"
              onClick={handleConfirmDelete}
              disabled={deleteCalculus.isPending}
            >
              {deleteCalculus.isPending ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {t('addAccount.deleting')}
                </>
              ) : (
                t('addAccount.delete')
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

export default AddAccount; 